import React from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
  HStack,
  Link,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@apollo/client';
import { GET_TENANT_CONTACT_FOR_AUTH } from '../queries';
import { type AuthMethod, type LookupFailedInfo } from '../types';

interface NoAccountFoundCardProps {
  info: LookupFailedInfo;
  // Theme colors
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accentBlue: string;
  // Handlers
  onTryDifferent: () => void;
  onSwitchMethod?: (method: AuthMethod) => void;
  alternativeMethod?: AuthMethod;
}

/**
 * Rendered when verifySmsCode/verifyEmailCode returns accountStatus === LOOKUP_FAILED.
 *
 * Three calls-to-action — every working pivot the user has from this dead-end:
 *  1. Email tenant support (mailto: link with prefilled subject)
 *  2. Try a different phone/email
 *  3. Switch to the alternative auth method (e.g. SMS user falls back to email)
 *
 * Tenant copy is read from currentTenant — zero hard-coded tenant strings, so this
 * component ships unchanged across every tenant via the Module Upgrade Matrix.
 */
export const NoAccountFoundCard: React.FC<NoAccountFoundCardProps> = ({
  info,
  textPrimary,
  textSecondary,
  textMuted,
  accentBlue,
  onTryDifferent,
  onSwitchMethod,
  alternativeMethod,
}) => {
  const { data } = useQuery(GET_TENANT_CONTACT_FOR_AUTH, { fetchPolicy: 'cache-first' });
  const tenantName = data?.currentTenant?.name || 'this site';
  const supportEmail =
    data?.currentTenant?.emailConfig?.replyToEmail ||
    data?.currentTenant?.emailConfig?.fromEmail ||
    '';

  const subject = encodeURIComponent(`Account help — ${info.identifier}`);
  const body = encodeURIComponent(
    `Hi ${tenantName} team,\n\n` +
      `I tried to sign in with ${info.method === 'phone' ? 'phone number' : 'email'} ${info.identifier} ` +
      `but the system says no account exists. Could you check whether I have an account, or invite me if not?\n\n` +
      `Thanks.`
  );
  const mailto = supportEmail
    ? `mailto:${supportEmail}?subject=${subject}&body=${body}`
    : undefined;

  const altLabel = alternativeMethod === 'phone' ? 'phone' : alternativeMethod === 'email' ? 'email' : null;

  return (
    <VStack align="stretch" spacing={4}>
      <Alert
        status="warning"
        variant="left-accent"
        borderRadius="md"
        flexDirection="column"
        alignItems="flex-start"
      >
        <HStack spacing={2} align="flex-start">
          <AlertIcon mt={1} />
          <Box>
            <AlertTitle color={textPrimary}>No account found</AlertTitle>
            <AlertDescription color={textSecondary} fontSize="sm">
              We couldn't find an account on <strong>{tenantName}</strong> for{' '}
              <strong>{info.identifier}</strong>.
            </AlertDescription>
          </Box>
        </HStack>
      </Alert>

      <Text fontSize="sm" color={textSecondary}>
        If you think this is a mistake, contact <strong>{tenantName}</strong> and ask them to
        check or invite you:
      </Text>

      {supportEmail ? (
        <Tooltip label={`Open your mail client and email ${supportEmail}`} hasArrow openDelay={300}>
          <Button as={Link} href={mailto} bg={accentBlue} color="white" width="100%" _hover={{ textDecoration: 'none' }}>
            ✉️ Email {tenantName} support
          </Button>
        </Tooltip>
      ) : (
        <Text fontSize="xs" color={textMuted} textAlign="center">
          {tenantName} hasn't set a public support address yet. Try one of the options below.
        </Text>
      )}

      <HStack spacing={3} justify="center" wrap="wrap">
        <Tooltip label={`Go back and enter a different ${info.method === 'phone' ? 'phone number' : 'email address'}`} hasArrow openDelay={300}>
          <Link color={accentBlue} fontSize="sm" onClick={onTryDifferent} cursor="pointer">
            Try a different {info.method === 'phone' ? 'number' : 'email'}
          </Link>
        </Tooltip>
        {onSwitchMethod && altLabel && (
          <>
            <Text fontSize="sm" color={textMuted}>•</Text>
            <Tooltip label={`Sign in with ${altLabel} instead`} hasArrow openDelay={300}>
              <Link
                color={accentBlue}
                fontSize="sm"
                onClick={() => onSwitchMethod(alternativeMethod!)}
                cursor="pointer"
              >
                Use {altLabel} instead
              </Link>
            </Tooltip>
          </>
        )}
      </HStack>
    </VStack>
  );
};
